import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CIcon from "@coreui/icons-react";
import { cilChevronRight } from "@coreui/icons";
import {
  TradingStrategyStatTone,
  type TradingStrategyDto,
} from "../../../api/hooks/useTradingStrategies";
import { StrategySideLabel } from "./StrategySideLabel";

function toneColorClass(tone: TradingStrategyStatTone): string {
  switch (tone) {
    case TradingStrategyStatTone.Positive:
      return "text-success";
    case TradingStrategyStatTone.Negative:
      return "text-danger";
    default:
      return "";
  }
}

interface SideFigure {
  text: string;
  className: string;
}

function sideFigure(strategy: TradingStrategyDto, statKey: string): SideFigure {
  const stat = strategy.stats.find((candidate) => candidate.statKey === statKey);
  const value = stat?.numericValue;
  if (stat === undefined || value === null || value === undefined) {
    return { text: "-", className: "text-body-secondary" };
  }

  const sign = value > 0 ? "+" : "";
  return { text: `${sign}${value.toFixed(1)}%`, className: toneColorClass(stat.tone) };
}

export interface StrategySidesColumn {
  /** A CSS grid track, fixed so the column lines up from row to row. */
  width: string;
  heading: ReactNode;
  cell: (side: TradingStrategyDto) => ReactNode;
}

interface StrategySidesTableProps {
  sides: TradingStrategyDto[];
  /** Columns after the two figures. */
  endColumns?: StrategySidesColumn[];
  /** Where a side's row leads. Left out where the whole card is already the link. */
  sideHref?: (side: TradingStrategyDto) => string;
}

/** A strategy's sides, a row each, with the two figures that tell them apart. */
export function StrategySidesTable({ sides, endColumns = [], sideHref }: StrategySidesTableProps) {
  const { t } = useTranslation("tradingStrategies");

  const tracks = [
    "minmax(0, 1fr)",
    "4.75rem",
    "4rem",
    ...endColumns.map((column) => column.width),
    ...(sideHref ? ["1rem"] : []),
  ];
  const gridTemplateColumns = tracks.join(" ");

  return (
    <div className="strategy-sides">
      <div className="strategy-sides-head" style={{ gridTemplateColumns }}>
        <span>{t("card.sides.side")}</span>
        <span className="text-end text-nowrap">{t("card.sides.avg")}</span>
        <span className="text-end text-nowrap">{t("card.sides.maxDrawdown")}</span>
        {endColumns.map((column, index) => (
          <span key={index} className="text-end text-nowrap">
            {column.heading}
          </span>
        ))}
        {sideHref && <span />}
      </div>
      {sides.map((side) => {
        const avg = sideFigure(side, "avgAnnualProfit");
        const drawdown = sideFigure(side, "maxRealDrawdown");
        const cells = (
          <>
            {side.direction ? (
              <StrategySideLabel direction={side.direction} fit className="fw-medium" />
            ) : (
              <span className="text-truncate">{side.publicName}</span>
            )}
            <span className={`text-end ${avg.className}`}>{avg.text}</span>
            <span className={`text-end ${drawdown.className}`}>{drawdown.text}</span>
            {endColumns.map((column, index) => (
              <span key={index} className="text-end text-nowrap">
                {column.cell(side)}
              </span>
            ))}
          </>
        );

        if (!sideHref) {
          return (
            <div key={side.id} className="strategy-side-row" style={{ gridTemplateColumns }}>
              {cells}
            </div>
          );
        }

        // Inside a card that is itself a link, so the row's click and Enter stop here rather than
        // also opening the card's own side.
        return (
          <Link
            key={side.id}
            to={sideHref(side)}
            className="strategy-side-row"
            style={{ gridTemplateColumns }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {cells}
            <CIcon icon={cilChevronRight} className="card-link-chevron" aria-hidden />
          </Link>
        );
      })}
    </div>
  );
}
